import type { CacheBucket } from "@elaso/shared/schemas/platform";
import type { ExternalUser } from "@elaso/shared/types/organization";
import type {
	AssignRolesBulkResponse,
	EmailReboundRow,
	HeldMemberRow,
	PreviewExternalUsersResponse,
	RemovalAbortReason,
	RemovalPreviewRow,
	RosterSkipRow,
	SyncFailureRow,
} from "@elaso/shared/types/platform";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ulid } from "ulid";
import {
	EmailsReboundList,
	FailedUsersList,
	HeldMembersList,
	hasSkipIdentity,
	RemovedMembersList,
	RoleChip,
	type RoleChipState,
	removalAbortLabel,
	SkippedUsersList,
	skipKey,
} from "@/components/app/roster-sync-report";
import { OrganizationAuthorityDialog } from "@/components/platform/organization-authority-dialog";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { OrganizationOption } from "@/hooks/use-workspace-list";
import { ws } from "@/lib/websocket-wrapper";

type WorkspaceOption = { id: string; name: string };
type RoleOption = { id: string; name: string };

export const Route = createFileRoute("/platform/")({
	component: PlatformOperationsPage,
});

/**
 * The overview page: tenants, imports, and the two process controls. The guard,
 * the socket claim and the surface's scroll container all live on the layout
 * route (`platform/route.tsx`). Anything reached from here is already known to
 * be a signed-in platform admin on a live connection.
 */
function PlatformOperationsPage() {
	// Bumped whenever the Organizations card creates or toggles an org, so the
	// import section's org picker refetches instead of serving its mount-time
	// snapshot of the tenant list.
	const [organizationsVersion, setOrganizationsVersion] = useState(0);

	return (
		<div className="flex flex-col items-center gap-8 p-8">
			<OrganizationsSection
				onChanged={() => setOrganizationsVersion((v) => v + 1)}
			/>
			<ExternalWorkspacesSection organizationsVersion={organizationsVersion} />
			<AssignUserForm />
			<CacheControlsSection />
			<CpuProfileSection />
		</div>
	);
}

type OrganizationRow = {
	id: string;
	name: string;
	enabled: boolean;
	adapterKind: string | null;
};

// "none" is a UI-only choice; it sends no `adapterKind` at all.
type AdapterChoice = "strapi" | "lti" | "none";

// Display labels for the persisted `kind`. Indexed loosely so an unrecognised
// kind (a server ahead of this client) falls back to the raw value instead of
// rendering nothing.
const ADAPTER_KIND_LABELS: Record<string, string> = {
	strapi: "Strapi",
	lti: "LTI 1.3",
};

const JSON_INDENT = 2;

type AdapterRegistration = {
	kind: string;
	config: unknown;
	externalIssuer: string | null;
	externalClientId: string | null;
};

function OrganizationsSection({ onChanged }: { onChanged: () => void }) {
	const [organizations, setOrganizations] = useState<OrganizationRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [name, setName] = useState("");
	const [adapterKind, setAdapterKind] = useState<AdapterChoice>("strapi");
	const [submitting, setSubmitting] = useState(false);
	const [togglingId, setTogglingId] = useState<string | null>(null);

	const load = () => {
		setLoading(true);
		ws.sendWithPromise<{ organizations: OrganizationRow[] }>({
			id: ulid(),
			procedure: "instanceAdmin.listOrganizations",
			data: { includeDisabled: true },
		})
			.then((res) => setOrganizations(res.organizations))
			.catch((err: Error) => {
				toast.error(`Failed to load organizations: ${err.message}`);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		load();
	}, []);

	async function handleCreate(e: React.FormEvent) {
		e.preventDefault();
		const trimmed = name.trim();
		if (!trimmed || submitting) return;
		setSubmitting(true);
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "instanceAdmin.createOrganization",
				data:
					adapterKind === "none"
						? { name: trimmed }
						: { name: trimmed, adapterKind },
			});
			toast.success(`Created organization ${trimmed}`);
			setName("");
			load();
			onChanged();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Create failed: ${message}`);
		} finally {
			setSubmitting(false);
		}
	}

	async function handleToggle(organization: OrganizationRow) {
		setTogglingId(organization.id);
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "instanceAdmin.setOrganizationEnabled",
				data: {
					organizationId: organization.id,
					enabled: !organization.enabled,
				},
			});
			load();
			onChanged();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Update failed: ${message}`);
		} finally {
			setTogglingId(null);
		}
	}

	return (
		<Card className="w-full max-w-3xl">
			<CardHeader>
				<CardTitle>Organizations</CardTitle>
				<CardDescription>
					The tenants imports and workspace links act on. Every import names its
					organization explicitly; a disabled organization is refused everywhere
					and hidden from the import picker.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-6">
				{loading ? (
					<p className="text-muted-foreground text-sm">
						Loading organizations…
					</p>
				) : organizations.length === 0 ? (
					<p className="text-muted-foreground text-sm">
						No organizations yet — create the first one below.
					</p>
				) : (
					<ul className="divide-y">
						{organizations.map((organization) => (
							<OrganizationListItem
								key={organization.id}
								onSaved={load}
								onToggle={handleToggle}
								organization={organization}
								toggling={togglingId === organization.id}
							/>
						))}
					</ul>
				)}

				<form className="flex items-end gap-2" onSubmit={handleCreate}>
					<div className="flex-1 space-y-2">
						<Label htmlFor="new-organization-name">New organization</Label>
						<Input
							id="new-organization-name"
							onChange={(e) => setName(e.target.value)}
							placeholder="organization name"
							value={name}
						/>
					</div>
					<div className="w-44 space-y-2">
						<Label htmlFor="new-organization-adapter">Adapter</Label>
						<Select
							onValueChange={(v) => setAdapterKind(v as AdapterChoice)}
							value={adapterKind}
						>
							<SelectTrigger id="new-organization-adapter">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="strapi">Strapi</SelectItem>
								<SelectItem value="lti">LTI 1.3</SelectItem>
								<SelectItem value="none">No adapter</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<Button disabled={!name.trim() || submitting} type="submit">
						{submitting ? "Creating…" : "Create"}
					</Button>
				</form>
			</CardContent>
		</Card>
	);
}

/**
 * One row of the Organizations list. Extracted from the list body so the row's
 * own branching (adapter label, configure vs not, disable vs enable) does not
 * pile onto the card component's complexity.
 */
function OrganizationListItem({
	organization,
	toggling,
	onToggle,
	onSaved,
}: {
	organization: OrganizationRow;
	toggling: boolean;
	onToggle: (organization: OrganizationRow) => void;
	onSaved: () => void;
}) {
	const adapterLabel = organization.adapterKind
		? (ADAPTER_KIND_LABELS[organization.adapterKind] ??
			organization.adapterKind)
		: "No adapter";

	return (
		<li className="flex items-center justify-between gap-4 py-3">
			<div className="min-w-0">
				<div className="font-medium">{organization.name}</div>
				<div className="text-muted-foreground text-xs">
					{organization.enabled ? "Enabled" : "Disabled"} · {adapterLabel}
				</div>
			</div>
			<div className="flex shrink-0 items-center gap-2">
				<OrganizationAuthorityDialog organization={organization} />
				{organization.adapterKind ? (
					<AdapterRegistrationDialog
						onSaved={onSaved}
						organization={organization}
					/>
				) : null}
				{organization.enabled ? (
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button disabled={toggling} size="sm" variant="secondary">
								Disable
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									Disable {organization.name}?
								</AlertDialogTitle>
								<AlertDialogDescription>
									Every import, link, and reconcile naming this organization
									will be refused until it is re-enabled. Existing workspaces
									and users are untouched.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction onClick={() => onToggle(organization)}>
									Disable
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				) : (
					<Button
						disabled={toggling}
						onClick={() => onToggle(organization)}
						size="sm"
					>
						Enable
					</Button>
				)}
			</div>
		</li>
	);
}

/**
 * Edits one Organization's adapter registration (TECH-3180).
 *
 * The form mirrors how the row is stored rather than any one adapter's
 * vocabulary. The two columns a launch *routes* on get dedicated fields, and the
 * rest of the registration is the raw `config` object. That is deliberate, because
 * this screen then needs no per-adapter knowledge and does not have to change
 * when an adapter adds a config field. The server validates the JSON against the
 * owning adapter's schema, so the price is that field-level errors arrive on
 * save instead of as you type.
 */
function AdapterRegistrationDialog({
	organization,
	onSaved,
}: {
	organization: OrganizationRow;
	onSaved: () => void;
}) {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [issuer, setIssuer] = useState("");
	const [clientId, setClientId] = useState("");
	const [configText, setConfigText] = useState("{}");
	const [jsonError, setJsonError] = useState<string | null>(null);

	// Load on open rather than on mount. One request per org the admin actually
	// edits, instead of one per row rendered.
	useEffect(() => {
		if (!open) return;
		setLoading(true);
		ws.sendWithPromise<{ adapter: AdapterRegistration | null }>({
			id: ulid(),
			procedure: "instanceAdmin.getOrganizationAdapter",
			data: { organizationId: organization.id },
		})
			.then((res) => {
				setIssuer(res.adapter?.externalIssuer ?? "");
				setClientId(res.adapter?.externalClientId ?? "");
				setConfigText(
					JSON.stringify(res.adapter?.config ?? {}, null, JSON_INDENT)
				);
				setJsonError(null);
			})
			.catch((err: Error) => {
				toast.error(`Failed to load the registration: ${err.message}`);
			})
			.finally(() => setLoading(false));
	}, [open, organization.id]);

	async function handleSave() {
		let config: unknown;
		try {
			config = JSON.parse(configText);
		} catch {
			setJsonError("Not valid JSON.");
			return;
		}
		// The column holds an object for every adapter kind, so reject the shapes
		// JSON allows but the row does not before spending a round-trip on them.
		if (
			config === null ||
			typeof config !== "object" ||
			Array.isArray(config)
		) {
			setJsonError("The config must be a JSON object.");
			return;
		}
		setJsonError(null);
		setSaving(true);
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "instanceAdmin.updateOrganizationAdapter",
				data: {
					organizationId: organization.id,
					externalIssuer: issuer.trim(),
					externalClientId: clientId.trim(),
					config,
				},
			});
			toast.success(`Saved ${organization.name}'s registration`);
			setOpen(false);
			onSaved();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Save failed: ${message}`);
		} finally {
			setSaving(false);
		}
	}

	const kindLabel =
		ADAPTER_KIND_LABELS[organization.adapterKind ?? ""] ??
		organization.adapterKind;

	return (
		<Dialog onOpenChange={setOpen} open={open}>
			<DialogTrigger asChild>
				<Button size="sm" variant="outline">
					Configure
				</Button>
			</DialogTrigger>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>
						{organization.name} — {kindLabel} registration
					</DialogTitle>
					<DialogDescription>
						What the upstream admin handed over. The issuer and client ID are
						the pair every launch is routed by; everything else is this
						adapter's own config.
					</DialogDescription>
				</DialogHeader>

				{loading ? (
					<p className="text-muted-foreground text-sm">Loading…</p>
				) : (
					<div className="space-y-4">
						<div className="space-y-1.5">
							<Label htmlFor={`adapter-issuer-${organization.id}`}>
								Issuer (iss)
							</Label>
							<Input
								id={`adapter-issuer-${organization.id}`}
								onChange={(e) => setIssuer(e.target.value)}
								placeholder="https://canvas.instructure.com"
								value={issuer}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor={`adapter-client-id-${organization.id}`}>
								Client ID
							</Label>
							<Input
								id={`adapter-client-id-${organization.id}`}
								onChange={(e) => setClientId(e.target.value)}
								placeholder="10000000000123"
								value={clientId}
							/>
							<p className="text-muted-foreground text-xs">
								Set both or neither — half a pair routes nothing. Adapters whose
								credentials come from the environment (Strapi) leave both empty.
							</p>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor={`adapter-config-${organization.id}`}>
								Config (JSON)
							</Label>
							<Textarea
								className="font-mono text-xs"
								id={`adapter-config-${organization.id}`}
								onChange={(e) => setConfigText(e.target.value)}
								rows={12}
								spellCheck={false}
								value={configText}
							/>
							{jsonError ? (
								<p className="text-destructive text-xs">{jsonError}</p>
							) : (
								<p className="text-muted-foreground text-xs">
									Validated against the {kindLabel} adapter's schema on save.
								</p>
							)}
						</div>
					</div>
				)}

				<DialogFooter>
					<Button
						onClick={() => setOpen(false)}
						type="button"
						variant="secondary"
					>
						Cancel
					</Button>
					<Button
						disabled={loading || saving}
						onClick={handleSave}
						type="button"
					>
						{saving ? "Saving…" : "Save"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function AssignUserForm() {
	const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
	const [roles, setRoles] = useState<RoleOption[]>([]);
	const [email, setEmail] = useState("");
	const [workspaceId, setWorkspaceId] = useState<string>("");
	const [roleId, setRoleId] = useState<string>("");
	const [submitting, setSubmitting] = useState(false);
	const [rolesLoading, setRolesLoading] = useState(false);

	useEffect(() => {
		ws.sendWithPromise<{ workspaces: WorkspaceOption[] }>({
			id: ulid(),
			procedure: "instanceAdmin.listWorkspaces",
			data: {},
		})
			.then((res) => setWorkspaces(res.workspaces))
			.catch((err: Error) => {
				toast.error(`Failed to load workspaces: ${err.message}`);
			});
	}, []);

	useEffect(() => {
		setRoleId("");
		setRoles([]);
		if (!workspaceId) return;

		setRolesLoading(true);
		ws.sendWithPromise<{ roles: RoleOption[] }>({
			id: ulid(),
			procedure: "instanceAdmin.listWorkspaceRoles",
			data: { workspaceId },
		})
			.then((res) => setRoles(res.roles))
			.catch((err: Error) => {
				toast.error(`Failed to load roles: ${err.message}`);
			})
			.finally(() => setRolesLoading(false));
	}, [workspaceId]);

	const canSubmit = !!email && !!workspaceId && !!roleId && !submitting;

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!canSubmit) return;
		setSubmitting(true);
		try {
			const result = await ws.sendWithPromise<{
				ok: boolean;
				firebaseUid: string;
			}>({
				id: ulid(),
				procedure: "instanceAdmin.assignUserToWorkspace",
				data: { email, workspaceId, roleId },
			});
			toast.success(`Assigned ${email} (uid ${result.firebaseUid})`);
			setEmail("");
			setRoleId("");
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Assignment failed: ${message}`);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="flex w-full justify-center">
			<Card className="w-full max-w-lg">
				<CardHeader>
					<CardTitle>Assign user to workspace</CardTitle>
					<CardDescription>
						Platform operations — looks up the user by email and grants the
						selected role in the chosen workspace.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form className="space-y-4" onSubmit={handleSubmit}>
						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								onChange={(e) => setEmail(e.target.value)}
								placeholder="user@example.com"
								type="email"
								value={email}
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="workspace">Workspace</Label>
							<Select onValueChange={setWorkspaceId} value={workspaceId}>
								<SelectTrigger id="workspace">
									<SelectValue placeholder="Select a workspace" />
								</SelectTrigger>
								<SelectContent>
									{workspaces.map((w) => (
										<SelectItem key={w.id} value={w.id}>
											{w.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-2">
							<Label htmlFor="role">Role</Label>
							<Select
								disabled={!workspaceId || rolesLoading}
								onValueChange={setRoleId}
								value={roleId}
							>
								<SelectTrigger id="role">
									<SelectValue
										placeholder={
											workspaceId
												? rolesLoading
													? "Loading roles..."
													: "Select a role"
												: "Pick a workspace first"
										}
									/>
								</SelectTrigger>
								<SelectContent>
									{roles.map((r) => (
										<SelectItem key={r.id} value={r.id}>
											{r.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<Button className="w-full" disabled={!canSubmit} type="submit">
							{submitting ? "Assigning..." : "Assign"}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}

type CacheBucketOption = { id: "all" | CacheBucket; label: string };

const CACHE_BUCKET_OPTIONS: CacheBucketOption[] = [
	{ id: "all", label: "All caches" },
	{ id: "userRoles", label: "User roles" },
	{ id: "assetPermissions", label: "Asset permissions" },
	{ id: "workspaceParticipants", label: "Workspace participants" },
];

// Which kind of target input a bucket takes (empty = clear the whole bucket).
function targetModeFor(
	bucket: "all" | CacheBucket
): "none" | "workspace" | "user" | "asset" {
	switch (bucket) {
		case "all":
			return "none";
		case "workspaceParticipants":
			return "workspace";
		case "assetPermissions":
			return "asset";
		default:
			return "user"; // userRoles, email or firebase uid
	}
}

type WarmCacheResponse = {
	workspaces: number;
	entriesWarmed: number;
	failedWorkspaces: number;
	durationMs: number;
	bucket: "all" | CacheBucket;
	workspaceId?: string;
};

// A full warm re-enumerates every workspace against OpenFGA. Like the import
// RPCs, it can far outlive the WS wrapper's 30s default.
const WARM_RPC_TIMEOUT_MS = 10 * 60 * 1000;

// Radix Select items need non-empty values; sentinel for "no workspace scope".
const WORKSPACE_SCOPE_ALL = "__all__";

/** Confirm-dialog copy for the clear button, by precedence: target → workspace scope → whole bucket. */
function clearConfirmDescription(
	bucket: "all" | CacheBucket,
	bucketLabel: string,
	trimmedTarget: string,
	scopedWorkspaceName: string | undefined
): string {
	if (trimmedTarget && bucket !== "all") {
		return `This clears the "${bucketLabel}" entry for "${trimmedTarget}".`;
	}
	if (scopedWorkspaceName) {
		return bucket === "all"
			? `This clears every server cache entry belonging to workspace "${scopedWorkspaceName}". Other workspaces stay warm; cleared entries self-heal on the next read.`
			: `This clears the "${bucketLabel}" cache for workspace "${scopedWorkspaceName}". Other workspaces stay warm.`;
	}
	if (bucket === "all") {
		return "This clears EVERY server cache (user roles, asset permissions, workspace participants, user profiles). Entries are authoritative and self-heal on the next read, but this forces a cold re-fetch from OpenFGA/Strapi.";
	}
	return `This clears the entire "${bucketLabel}" cache.`;
}

function CacheControlsSection() {
	const [bucket, setBucket] = useState<"all" | CacheBucket>("all");
	const [target, setTarget] = useState("");
	const [workspaceScope, setWorkspaceScope] = useState(WORKSPACE_SCOPE_ALL);
	const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
	const [submitting, setSubmitting] = useState(false);
	const [warming, setWarming] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);

	// Workspaces for the `workspaceParticipants` target dropdown. Non-blocking, so
	// if it fails the rest of the controls still work (other buckets do not use it).
	useEffect(() => {
		ws.sendWithPromise<{ workspaces: WorkspaceOption[] }>({
			id: ulid(),
			procedure: "instanceAdmin.listWorkspaces",
			data: {},
		})
			.then((res) => setWorkspaces(res.workspaces))
			.catch(() => {
				/* ignore, the participant target is the only consumer */
			});
	}, []);

	// Switching bucket clears any stale target from the previous bucket.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset target on bucket change
	useEffect(() => {
		setTarget("");
	}, [bucket]);

	const targetMode = targetModeFor(bucket);
	const trimmedTarget = target.trim();
	const scopedWorkspaceId =
		workspaceScope === WORKSPACE_SCOPE_ALL ? undefined : workspaceScope;
	const scopedWorkspaceName = scopedWorkspaceId
		? (workspaces.find((w) => w.id === scopedWorkspaceId)?.name ??
			scopedWorkspaceId)
		: undefined;

	async function handleConfirm() {
		setConfirmOpen(false);
		setSubmitting(true);
		try {
			const result = await ws.sendWithPromise<{
				cleared: string[];
				target?: string;
				workspaceId?: string;
			}>({
				id: ulid(),
				procedure: "instanceAdmin.clearCache",
				data: {
					bucket,
					target: trimmedTarget || undefined,
					// Target (a single entry) beats the workspace scope; the server
					// rejects requests carrying both.
					workspaceId: trimmedTarget ? undefined : scopedWorkspaceId,
				},
			});
			let scope = "";
			if (result.target) {
				scope = ` (${result.target})`;
			} else if (result.workspaceId) {
				scope = ` (workspace ${scopedWorkspaceName ?? result.workspaceId})`;
			}
			toast.success(`Cleared: ${result.cleared.join(", ")}${scope}`);
			setTarget("");
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Clear failed: ${message}`);
		} finally {
			setSubmitting(false);
		}
	}

	async function handleWarm() {
		setWarming(true);
		try {
			const result = await ws.sendWithPromise<WarmCacheResponse>({
				id: ulid(),
				procedure: "instanceAdmin.warmCache",
				data: {
					bucket,
					workspaceId: scopedWorkspaceId,
				},
				timeoutMs: WARM_RPC_TIMEOUT_MS,
			});
			const seconds = (result.durationMs / 1000).toFixed(1);
			const scopeLabel =
				result.bucket === "all"
					? ""
					: ` (${CACHE_BUCKET_OPTIONS.find((o) => o.id === result.bucket)?.label ?? result.bucket})`;
			const summary = `Warmed ${result.entriesWarmed} entries across ${result.workspaces} workspace(s)${scopeLabel} in ${seconds}s`;
			if (result.failedWorkspaces > 0) {
				toast.warning(
					`${summary} — ${result.failedWorkspaces} workspace warm(s) failed; those keys stay cold and self-heal on read`
				);
			} else {
				toast.success(summary);
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Warm failed: ${message}`);
		} finally {
			setWarming(false);
		}
	}

	const bucketLabel =
		CACHE_BUCKET_OPTIONS.find((o) => o.id === bucket)?.label ?? bucket;
	const confirmDescription = clearConfirmDescription(
		bucket,
		bucketLabel,
		trimmedTarget,
		scopedWorkspaceName
	);

	return (
		<div className="flex w-full justify-center">
			<Card className="w-full max-w-lg">
				<CardHeader>
					<CardTitle>Cache controls</CardTitle>
					<CardDescription>
						Internal — clear server caches completely, by bucket, by workspace,
						or a single targeted entry. Cleared entries self-heal on the next
						read. Warming repopulates caches up front (same pass as server
						start) — all of them, one bucket, or one workspace — so reads don't
						pay the cold-miss cost.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="cache-bucket">Bucket</Label>
							<Select
								onValueChange={(v) => setBucket(v as "all" | CacheBucket)}
								value={bucket}
							>
								<SelectTrigger id="cache-bucket">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{CACHE_BUCKET_OPTIONS.map((o) => (
										<SelectItem key={o.id} value={o.id}>
											{o.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{targetMode === "none" ? null : (
							<div className="space-y-2">
								<Label htmlFor="cache-target">
									Target{" "}
									<span className="font-normal text-muted-foreground">
										(optional — leave empty to clear the whole bucket)
									</span>
								</Label>
								{targetMode === "workspace" ? (
									<Select onValueChange={setTarget} value={target}>
										<SelectTrigger id="cache-target">
											<SelectValue placeholder="All workspaces (entire bucket)" />
										</SelectTrigger>
										<SelectContent>
											{workspaces.map((w) => (
												<SelectItem key={w.id} value={w.id}>
													{w.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								) : (
									<Input
										id="cache-target"
										onChange={(e) => setTarget(e.target.value)}
										placeholder={
											targetMode === "asset"
												? "asset key, e.g. asset:…--channel--…"
												: "email or firebase uid"
										}
										value={target}
									/>
								)}
							</div>
						)}

						<div className="space-y-2">
							<Label htmlFor="workspace-scope">
								Workspace scope{" "}
								<span className="font-normal text-muted-foreground">
									(applies to Clear and Warm; a Target above overrides it for
									Clear)
								</span>
							</Label>
							<Select onValueChange={setWorkspaceScope} value={workspaceScope}>
								<SelectTrigger id="workspace-scope">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={WORKSPACE_SCOPE_ALL}>
										All workspaces
									</SelectItem>
									{workspaces.map((w) => (
										<SelectItem key={w.id} value={w.id}>
											{w.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<AlertDialog onOpenChange={setConfirmOpen} open={confirmOpen}>
							<AlertDialogTrigger asChild>
								<Button
									className="w-full"
									disabled={submitting || warming}
									variant="destructive"
								>
									{submitting ? "Clearing…" : "Clear cache"}
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Clear cache?</AlertDialogTitle>
									<AlertDialogDescription>
										{confirmDescription}
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>Cancel</AlertDialogCancel>
									<AlertDialogAction onClick={handleConfirm}>
										Clear
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>

						{/* Warm honors the Bucket + Workspace scope selects (but not the
						    clear-specific Target input). Additive and idempotent
						    (already-warm keys are skipped), so no confirmation gate. */}
						<Button
							className="w-full"
							disabled={submitting || warming}
							onClick={handleWarm}
							variant="secondary"
						>
							{warming ? "Warming… (can take a few minutes)" : "Warm cache"}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

type ExternalWorkspace = {
	externalRef: string;
	name: string;
	existingWorkspaceId: string | null;
	existingWorkspaceName: string | null;
};

// The roster row shapes and the preview response are the shared wire contract
// (`@elaso/shared/types/platform`, imported above). This file used to carry
// local copies, and its `ExternalUser` had drifted from the server's.

type Preview = {
	externalRef: string;
	externalWorkspaceName: string;
	users: ExternalUser[];
	workspaceName: string;
	existingWorkspaceId: string | null;
	existingWorkspaceName: string | null;
	existingUserRefs: Set<string>;
	currentRolesByUser: Record<string, string[]> | null;
	wouldRemove: RemovalPreviewRow[] | null;
	wouldHold: HeldMemberRow[] | null;
	wouldRebindEmails: EmailReboundRow[];
	removalsWouldAbort: RemovalAbortReason | null;
	search: string;
};

type ImportResult = {
	imported: {
		created: number;
		updated: number;
		skipped: number;
		total: number;
		skippedReport: RosterSkipRow[];
		// The gate's email convergence (TECH-3246). The import phase can rebind a
		// drifted email, and can fail per user doing it.
		emailsRebound: EmailReboundRow[];
		failed: SyncFailureRow[];
	};
	/**
	 * The reconcile half, straight from the server's own contract
	 * (TECH-3262). Restating it here is what let a renamed field type-check on
	 * one side and arrive `undefined` on the other.
	 */
	roles: AssignRolesBulkResponse;
};

// The two phases run the same gate against two separate roster fetches, so
// both can report rebinds and failures for one run. In the steady state the
// import's rebind clears the drift before the reconcile refetches, but a
// phase-1 fault retried by phase 2 would repeat a row, so both unions dedupe.
const failureKey = (f: SyncFailureRow) => JSON.stringify([f.email, f.error]);
function mergedRunFailures(result: ImportResult): SyncFailureRow[] {
	const seen = new Set(result.imported.failed.map(failureKey));
	return [
		...result.imported.failed,
		...result.roles.failed.filter((f) => !seen.has(failureKey(f))),
	];
}
function mergedEmailsRebound(result: ImportResult): EmailReboundRow[] {
	// One user can rebind in BOTH phases (old→a at import, then a→b when the
	// reconcile refetches a roster that moved again mid-run). Chain the rows,
	// first previousEmail and last newEmail, so the report shows the address the
	// login actually ended on.
	const byRef = new Map<string, EmailReboundRow>();
	for (const row of [
		...result.imported.emailsRebound,
		...result.roles.emailsRebound,
	]) {
		const prior = byRef.get(row.externalRef);
		byRef.set(
			row.externalRef,
			prior ? { ...prior, newEmail: row.newEmail } : row
		);
	}
	return [...byRef.values()];
}

type RunStateUI = {
	status: "running" | "success" | "failed";
	currentLabel: string;
	error: string | null;
	result: ImportResult | null;
};

function ExternalWorkspacesSection({
	organizationsVersion,
}: {
	organizationsVersion: number;
}) {
	const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
	const [organizationsLoading, setOrganizationsLoading] = useState(true);
	const [selectedOrganizationId, setSelectedOrganizationId] = useState<
		string | null
	>(null);
	const [externalWorkspaces, setExternalWorkspaces] = useState<
		ExternalWorkspace[]
	>([]);
	const [loading, setLoading] = useState(false);
	const [pendingExternalRef, setPendingExternalRef] = useState<string | null>(
		null
	);
	const [previewLoadingRef, setPreviewLoadingRef] = useState<string | null>(
		null
	);
	const [preview, setPreview] = useState<Preview | null>(null);
	const [runState, setRunState] = useState<RunStateUI | null>(null);

	// Every import/link procedure names its Organization explicitly (TECH-3206);
	// the server never defaults it. Exactly one enabled org → pre-select it so
	// the single-org flow is unchanged; several → the admin must pick first.
	// Refetches whenever the Organizations card changes the tenant list, and
	// drops the selection if the selected org is no longer a legal target.
	useEffect(() => {
		ws.sendWithPromise<{ organizations: OrganizationOption[] }>({
			id: ulid(),
			procedure: "instanceAdmin.listOrganizations",
			data: {},
		})
			.then((res) => {
				setOrganizations(res.organizations);
				if (res.organizations.length === 1) {
					setSelectedOrganizationId(res.organizations[0].id);
				} else {
					setSelectedOrganizationId((current) =>
						current && res.organizations.some((o) => o.id === current)
							? current
							: null
					);
				}
			})
			.catch((err: Error) => {
				toast.error(`Failed to load organizations: ${err.message}`);
			})
			.finally(() => setOrganizationsLoading(false));
	}, [organizationsVersion]);

	const load = (organizationId: string) => {
		setLoading(true);
		ws.sendWithPromise<{ externalWorkspaces: ExternalWorkspace[] }>({
			id: ulid(),
			procedure: "instanceAdmin.listExternalWorkspaces",
			data: { organizationId },
		})
			.then((res) => setExternalWorkspaces(res.externalWorkspaces))
			.catch((err: Error) => {
				toast.error(`Failed to load external workspaces: ${err.message}`);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		if (selectedOrganizationId) {
			load(selectedOrganizationId);
		}
	}, [selectedOrganizationId]);

	async function handlePreview(externalWorkspace: ExternalWorkspace) {
		if (!selectedOrganizationId) return;
		setPreviewLoadingRef(externalWorkspace.externalRef);
		setPreview(null);
		try {
			const result = await ws.sendWithPromise<PreviewExternalUsersResponse>({
				id: ulid(),
				procedure: "instanceAdmin.previewExternalUsers",
				data: {
					organizationId: selectedOrganizationId,
					externalRef: externalWorkspace.externalRef,
				},
			});
			setPreview({
				externalRef: result.externalWorkspace.externalRef,
				externalWorkspaceName: result.externalWorkspace.name,
				users: result.users,
				workspaceName:
					externalWorkspace.existingWorkspaceName ?? externalWorkspace.name,
				existingWorkspaceId: externalWorkspace.existingWorkspaceId,
				existingWorkspaceName: externalWorkspace.existingWorkspaceName,
				existingUserRefs: new Set(result.existingUserRefs),
				currentRolesByUser: result.currentRolesByUser,
				wouldRemove: result.wouldRemove,
				wouldHold: result.wouldHold,
				wouldRebindEmails: result.wouldRebindEmails,
				removalsWouldAbort: result.removalsWouldAbort,
				search: "",
			});
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Preview failed: ${message}`);
		} finally {
			setPreviewLoadingRef(null);
		}
	}

	// Each import step is bounded by OpenFGA throughput and Strapi pagination.
	// A multi-thousand-user reconcile can take a couple of minutes. Override the
	// WS wrapper's 30s default for these three calls only.
	const IMPORT_RPC_TIMEOUT_MS = 5 * 60 * 1000;

	async function runImport(current: Preview) {
		if (!selectedOrganizationId) return;
		setPendingExternalRef(current.externalRef);
		setRunState({
			status: "running",
			currentLabel: current.existingWorkspaceId
				? "Importing users…"
				: "Creating workspace…",
			error: null,
			result: null,
		});

		try {
			// 1. Resolve or create the target workspace.
			let workspaceId: string;
			if (current.existingWorkspaceId) {
				workspaceId = current.existingWorkspaceId;
			} else {
				const { workspace } = await ws.sendWithPromise<{
					workspace: { id: string; name: string };
				}>({
					id: ulid(),
					procedure: "instanceAdmin.createWorkspaceFromExternal",
					data: {
						organizationId: selectedOrganizationId,
						externalRef: current.externalRef,
						name: current.workspaceName.trim(),
					},
					timeoutMs: IMPORT_RPC_TIMEOUT_MS,
				});
				workspaceId = workspace.id;
				setRunState((prev) =>
					prev ? { ...prev, currentLabel: "Importing users…" } : prev
				);
			}

			// 2. Import users into MongoDB.
			const imported = await ws.sendWithPromise<ImportResult["imported"]>({
				id: ulid(),
				procedure: "instanceAdmin.importUsersForWorkspace",
				data: { organizationId: selectedOrganizationId, workspaceId },
				timeoutMs: IMPORT_RPC_TIMEOUT_MS,
			});

			// 3. Assign roles in OpenFGA.
			setRunState((prev) =>
				prev ? { ...prev, currentLabel: "Assigning roles…" } : prev
			);
			const roles = await ws.sendWithPromise<AssignRolesBulkResponse>({
				id: ulid(),
				procedure: "instanceAdmin.assignRolesBulk",
				data: { organizationId: selectedOrganizationId, workspaceId },
				timeoutMs: IMPORT_RPC_TIMEOUT_MS,
			});

			setRunState({
				status: "success",
				currentLabel: "Done",
				error: null,
				result: { imported, roles },
			});
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			setRunState({
				status: "failed",
				currentLabel: "Failed",
				error: message,
				result: null,
			});
		}
	}

	async function handleConfirm() {
		if (!preview) return;
		await runImport(preview);
	}

	async function handleRetry() {
		if (!preview) return;
		await runImport(preview);
	}

	function renderBody() {
		if (organizationsLoading) {
			return (
				<p className="text-muted-foreground text-sm">Loading organizations…</p>
			);
		}
		if (organizations.length === 0) {
			return (
				<p className="text-muted-foreground text-sm">
					No enabled organizations found.
				</p>
			);
		}
		if (!selectedOrganizationId) {
			return (
				<p className="text-muted-foreground text-sm">
					Select an organization to load its external workspaces.
				</p>
			);
		}
		if (loading) {
			return (
				<p className="text-muted-foreground text-sm">
					Loading external workspaces…
				</p>
			);
		}
		if (externalWorkspaces.length === 0) {
			return (
				<p className="text-muted-foreground text-sm">
					No external workspaces found.
				</p>
			);
		}
		return (
			<ul className="divide-y">
				{externalWorkspaces.map((externalWorkspace) => (
					<ExternalWorkspaceRow
						externalWorkspace={externalWorkspace}
						key={externalWorkspace.externalRef}
						onPreview={() => handlePreview(externalWorkspace)}
						pending={pendingExternalRef === externalWorkspace.externalRef}
						previewLoading={previewLoadingRef === externalWorkspace.externalRef}
					/>
				))}
			</ul>
		);
	}

	return (
		<>
			<Card className="w-full max-w-3xl">
				<CardHeader>
					<CardTitle>External workspaces</CardTitle>
					<CardDescription>
						Preview an upstream workspace's users, then create the ELASO
						workspace (if needed) and import them.
					</CardDescription>
				</CardHeader>
				<CardContent>
					{organizations.length > 1 ? (
						<div className="mb-4 max-w-xs space-y-2">
							<Label htmlFor="import-organization">Organization</Label>
							<Select
								onValueChange={setSelectedOrganizationId}
								value={selectedOrganizationId ?? undefined}
							>
								<SelectTrigger id="import-organization">
									<SelectValue placeholder="Select an organization" />
								</SelectTrigger>
								<SelectContent>
									{organizations.map((organization) => (
										<SelectItem key={organization.id} value={organization.id}>
											{organization.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					) : null}
					{renderBody()}
				</CardContent>
			</Card>
			{preview ? (
				<PreviewDialog
					onCancel={() => {
						if (runState?.status === "running") return;
						setPreview(null);
						setRunState(null);
						setPendingExternalRef(null);
						// On success the workspace-list state (link status, etc.)
						// changes; refresh on dismiss.
						if (runState?.status === "success" && selectedOrganizationId) {
							load(selectedOrganizationId);
						}
					}}
					onConfirm={handleConfirm}
					onRetry={handleRetry}
					onUpdatePreview={(updates) => setPreview({ ...preview, ...updates })}
					pending={pendingExternalRef === preview.externalRef}
					preview={preview}
					runState={runState}
				/>
			) : null}
		</>
	);
}

function ExternalWorkspaceRow({
	externalWorkspace,
	pending,
	previewLoading,
	onPreview,
}: {
	externalWorkspace: ExternalWorkspace;
	pending: boolean;
	previewLoading: boolean;
	onPreview: () => void;
}) {
	const linked = externalWorkspace.existingWorkspaceId !== null;
	let label = linked ? "Preview & import" : "Preview users";
	if (previewLoading) label = "Loading…";
	return (
		<li className="flex items-center justify-between gap-4 py-3">
			<div className="min-w-0">
				<div className="font-medium">{externalWorkspace.name}</div>
				<div className="truncate text-muted-foreground text-xs">
					{externalWorkspace.externalRef}
					{externalWorkspace.existingWorkspaceName
						? ` • Linked to ${externalWorkspace.existingWorkspaceName}`
						: ""}
				</div>
			</div>
			<div className="flex shrink-0 gap-2">
				<Button
					disabled={previewLoading || pending}
					onClick={onPreview}
					size="sm"
					variant={linked ? "default" : "secondary"}
				>
					{label}
				</Button>
			</div>
		</li>
	);
}

type PreviewDialogProps = {
	preview: Preview;
	pending: boolean;
	runState: RunStateUI | null;
	onUpdatePreview: (updates: Partial<Preview>) => void;
	onConfirm: () => void;
	onCancel: () => void;
	onRetry: () => void;
};

// The preview snapshot's promise yields to the result's fact. After a
// successful run the result panels below the footer tell the truth, and a
// lingering "will be removed"/"will be kept" reads as a second pending pass.
// Both stay visible before, during, and after a FAILED run, where the
// removal may genuinely still be ahead.
function PreviewRemovalPanels({
	preview,
	runState,
}: {
	preview: Preview;
	runState: RunStateUI | null;
}) {
	if (runState?.status === "success") {
		return null;
	}
	return (
		<>
			{/* TECH-3246: the identity writes a confirmed import will make,
			    announced up front. Approving an email rewrite the preview never
			    mentioned is bad consent. Same yield-to-result rule as the removal
			    panels above. */}
			{preview.wouldRebindEmails.length > 0 ? (
				<EmailsReboundList rows={preview.wouldRebindEmails} tense="will" />
			) : null}
			{/* The abort the confirmed sync WOULD hit, shown before anyone
			    confirms. A silent absence of removal panels reads as "the
			    roster matches", which is not what an aborted pass means. */}
			{preview.removalsWouldAbort ? (
				<div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
					<span className="font-medium text-amber-600">
						No members will be removed:
					</span>{" "}
					<span className="text-muted-foreground">
						{removalAbortLabel(preview.removalsWouldAbort)} The sync will stay
						add-only; removals resume once the roster reads correctly.
					</span>
				</div>
			) : null}
			{preview.wouldRemove && preview.wouldRemove.length > 0 ? (
				<RemovedMembersList
					rows={preview.wouldRemove.map((m) => ({
						externalRef: m.externalRef,
						email: m.email,
						displayName: m.displayName,
						roles: m.currentRoles,
					}))}
					tense="will"
				/>
			) : null}
			{preview.wouldHold && preview.wouldHold.length > 0 ? (
				<HeldMembersList held={preview.wouldHold} tense="will" />
			) : null}
		</>
	);
}

function PreviewDialog({
	preview,
	pending,
	runState,
	onUpdatePreview,
	onConfirm,
	onCancel,
	onRetry,
}: PreviewDialogProps) {
	const filteredUsers = useMemo(() => {
		const q = preview.search.trim().toLowerCase();
		if (!q) return preview.users;
		return preview.users.filter(
			(u) =>
				u.displayName.toLowerCase().includes(q) ||
				u.email.toLowerCase().includes(q) ||
				u.externalRef.toLowerCase().includes(q)
		);
	}, [preview.search, preview.users]);

	const isReimport = preview.currentRolesByUser !== null;

	// The body is ONE scroll container (no nested mini-scrollbars). When a run
	// finishes, its result renders into that flow below the (stale) preview, so
	// bring it into view rather than leaving the outcome below the fold.
	const resultRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		if (runState?.status === "success") {
			resultRef.current?.scrollIntoView({ block: "start" });
		}
	}, [runState?.status]);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
			<Card className="flex max-h-[90vh] w-full max-w-6xl flex-col">
				<CardHeader>
					<CardTitle>{preview.externalWorkspaceName}</CardTitle>
					<CardDescription>
						{preview.users.length} users from this external workspace. Roles are
						taken from the upstream system; ELASO reconciles to match.
					</CardDescription>
				</CardHeader>
				{/* The single scroll zone: roster, removal panels, and run results all
				    flow here full-length. The status line + actions live in the pinned
				    footer below, so Confirm/Done never scrolls away. */}
				<CardContent className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
					{/* Sticky within the scroll zone: filtering stays one keystroke away
					    however deep the list is, with a count for orientation. pb-3/-mb-3
					    extends the masking background over the flex gap so rows don't
					    peek through beneath it while scrolling. */}
					<div className="-mb-3 sticky top-0 z-10 flex items-center gap-3 bg-card pb-3">
						<Input
							className="max-w-xs"
							onChange={(e) => onUpdatePreview({ search: e.target.value })}
							placeholder="Search name, email, id…"
							value={preview.search}
						/>
						<span className="text-muted-foreground text-sm">
							{filteredUsers.length === preview.users.length
								? `${preview.users.length} user${preview.users.length === 1 ? "" : "s"}`
								: `${filteredUsers.length} of ${preview.users.length} users shown`}
						</span>
					</div>

					{/* shrink-0 is load-bearing: an overflow≠visible flex child has an
					    automatic min-height of ZERO, so without it the flex column
					    squeezes this table toward nothing to fit the other children
					    before the scroll zone ever scrolls. */}
					<div className="shrink-0 overflow-x-auto rounded-md border">
						<table className="w-full text-sm">
							{/* Not sticky: the header's positioning ancestor is the
							    overflow-x wrapper (which never scrolls vertically), and a
							    sticky top-0 here would collide with the sticky filter bar
							    if that ever changed. */}
							<thead className="bg-muted">
								<tr className="text-left">
									<th className="px-3 py-2 font-medium">Display name</th>
									<th className="px-3 py-2 font-medium">Email</th>
									<th className="px-3 py-2 font-medium">Roles</th>
								</tr>
							</thead>
							<tbody className="divide-y">
								{filteredUsers.length === 0 ? (
									<tr>
										<td
											className="px-3 py-4 text-center text-muted-foreground"
											colSpan={3}
										>
											{preview.users.length === 0
												? "No users in this program."
												: "No users match the filter."}
										</td>
									</tr>
								) : (
									filteredUsers.map((u) => (
										<UserRow
											alreadyInWorkspace={preview.existingUserRefs.has(
												u.externalRef
											)}
											currentRoles={preview.currentRolesByUser?.[u.externalRef]}
											isReimport={isReimport}
											key={u.externalRef}
											user={u}
										/>
									))
								)}
							</tbody>
						</table>
					</div>

					<PreviewRemovalPanels preview={preview} runState={runState} />

					{preview.existingWorkspaceId ? (
						<p className="text-muted-foreground text-sm">
							Will reconcile roles in existing workspace{" "}
							<span className="font-medium text-foreground">
								{preview.existingWorkspaceName}
							</span>
							. Added selections write new tuples; removed ones delete tuples.
						</p>
					) : (
						<div className="space-y-2">
							<Label htmlFor="workspace-name">Workspace name</Label>
							<Input
								disabled={pending}
								id="workspace-name"
								onChange={(e) =>
									onUpdatePreview({ workspaceName: e.target.value })
								}
								value={preview.workspaceName}
							/>
						</div>
					)}

					{runState?.status === "success" && runState.result ? (
						<div ref={resultRef}>
							<RunSuccessStats result={runState.result} />
						</div>
					) : null}
				</CardContent>

				{/* Pinned footer: always visible regardless of how long the data above
				    scrolls, so the status line and the actions never leave the screen. */}
				<div className="flex flex-col gap-3 border-t px-6 py-4">
					{runState ? (
						<RunProgressFooter
							onClose={onCancel}
							onRetry={onRetry}
							runState={runState}
						/>
					) : (
						<div className="flex justify-end gap-2">
							<Button
								disabled={pending}
								onClick={onCancel}
								size="sm"
								variant="ghost"
							>
								Cancel
							</Button>
							<Button
								disabled={
									pending ||
									(!preview.existingWorkspaceId &&
										preview.workspaceName.trim().length === 0)
								}
								onClick={onConfirm}
								size="sm"
							>
								{confirmLabel(preview, pending)}
							</Button>
						</div>
					)}
				</div>
			</Card>
		</div>
	);
}

function confirmLabel(preview: Preview, pending: boolean): string {
	if (pending) {
		return preview.existingWorkspaceId ? "Importing…" : "Creating…";
	}
	if (preview.existingWorkspaceId) {
		return `Import ${preview.users.length} users & reconcile roles`;
	}
	return "Create workspace, import & assign roles";
}

function UserRow({
	user,
	alreadyInWorkspace,
	currentRoles,
	isReimport,
}: {
	user: ExternalUser;
	alreadyInWorkspace: boolean;
	currentRoles: string[] | undefined;
	isReimport: boolean;
}) {
	// On a re-import, diff the adapter's authoritative role set against the
	// user's current OpenFGA tuples. On a first import, every role is just
	// being added, so there is no diff visualization.
	const currentSet = new Set(currentRoles ?? []);
	const targetSet = new Set(user.roles);
	const removing = isReimport
		? [...currentSet].filter((r) => !targetSet.has(r))
		: [];

	return (
		<tr>
			<td className="px-3 py-2 align-top">
				<div>{user.displayName}</div>
				{alreadyInWorkspace ? (
					<div className="text-muted-foreground text-xs">
						already in workspace
					</div>
				) : null}
			</td>
			<td className="px-3 py-2 align-top">{user.email || "—"}</td>
			<td className="px-3 py-2 align-top">
				<div className="flex flex-wrap gap-1">
					{user.roles.map((roleName) => {
						const state: RoleChipState = isReimport
							? currentSet.has(roleName)
								? "keeping"
								: "adding"
							: "neutral";
						return <RoleChip key={roleName} name={roleName} state={state} />;
					})}
					{removing.map((roleName) => (
						<RoleChip key={`rm-${roleName}`} name={roleName} state="removing" />
					))}
				</div>
			</td>
		</tr>
	);
}

function RunProgressFooter({
	runState,
	onClose,
	onRetry,
}: {
	runState: RunStateUI;
	onClose: () => void;
	onRetry: () => void;
}) {
	const isRunning = runState.status === "running";
	const isFailed = runState.status === "failed";
	const isSuccess = runState.status === "success";
	const failedCount = runState.result
		? mergedRunFailures(runState.result).length
		: 0;
	let statusLabel = runState.currentLabel;
	if (isSuccess) {
		statusLabel =
			failedCount > 0
				? `Done, with ${failedCount} user ${failedCount === 1 ? "failure" : "failures"} — details above`
				: "Done — results above";
	}
	return (
		<div className="flex flex-col gap-3">
			{/* A live region (<output> carries role="status" implicitly): the
			    label flips between "Assigning roles…", "Done" and failure without
			    a DOM remount, and a multi-minute run whose finish is never
			    announced strands a screen-reader user. */}
			<output aria-live="polite" className="flex items-center gap-2 text-sm">
				{isRunning ? (
					<span
						aria-hidden
						className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
					/>
				) : null}
				{isFailed ? <span aria-hidden>✗</span> : null}
				{isSuccess ? <span aria-hidden>✓</span> : null}
				<span
					className={
						isFailed
							? "font-medium text-destructive"
							: "font-medium text-foreground"
					}
				>
					{statusLabel}
				</span>
			</output>
			{isFailed && runState.error ? (
				<p className="text-destructive text-sm">{runState.error}</p>
			) : null}
			<div className="flex justify-end gap-2">
				{isFailed || (isSuccess && failedCount > 0) ? (
					<Button onClick={onRetry} size="sm">
						Retry
					</Button>
				) : null}
				<Button
					disabled={isRunning}
					onClick={onClose}
					size="sm"
					variant={isRunning ? "ghost" : "default"}
				>
					{isSuccess ? "Done" : "Close"}
				</Button>
			</div>
		</div>
	);
}

function RunSuccessStats({ result }: { result: ImportResult }) {
	const { imported, roles } = result;
	const failed = mergedRunFailures(result);
	const emailsRebound = mergedEmailsRebound(result);
	// The import and the reconcile report the same gate skips from two separate
	// roster fetches. Render the union once rather than the list twice
	// (restores the reconcile-phase rows the UI used to drop, TECH-3195 gap).
	const seenSkips = new Set(imported.skippedReport.map(skipKey));
	const skipped = [
		...imported.skippedReport,
		// Dedupe only rows with a stable identity, because two identifier-less
		// skips are not provably the same user, so both stay visible.
		...roles.skippedReport.filter(
			(s) => !(hasSkipIdentity(s) && seenSkips.has(skipKey(s)))
		),
	];
	// The Skipped statistic matches the merged list: `imported.skipped` folds the
	// idempotent no-ops (unchanged rows) with the import phase's problem-skips,
	// so recover the no-op count and add the merged problem-skips. The roles
	// phase reads the roster separately and can skip users the import did not.
	const skippedStat =
		imported.skipped - imported.skippedReport.length + skipped.length;
	return (
		<div className="space-y-3">
			<div className="grid grid-cols-2 gap-6">
				<StatGroup
					rows={[
						// Both phases can write User rows (TECH-3237), so the Users
						// column sums them. A user who arrived between the two roster
						// fetches is created by the reconcile and counted here. Only
						// these two fold, because the reconcile's `unchanged` is every
						// row the import already wrote, and folding it would inflate
						// Skipped by a whole roster.
						["Created", imported.created + roles.usersCreated],
						["Updated", imported.updated + roles.usersUpdated],
						["Emails updated", emailsRebound.length],
						["Skipped", skippedStat],
						// Named for the fetch it came from, not "Total". It is the size
						// of the roster the IMPORT read, while the rows above can also
						// count a user the reconcile's later fetch was the first to
						// see. Labelled as a combined total it could read lower than the
						// Created above it. The two fetches see two rosters, so no
						// honest single total exists to compute here.
						["Import roster", imported.total],
					]}
					title="Users"
				/>
				<StatGroup
					rows={[
						["Added", roles.added],
						["Removed", roles.removed],
						["Users touched", roles.usersTouched],
						["Members removed", roles.removedReport.length],
						["Members held", roles.heldReport.length],
						// Named for the phase that read it, like `Import roster` in the
						// group beside this one, and for the same reason. This is the
						// RECONCILE's roster fetch, a different read from the import's,
						// so the two can disagree. It was labelled "Total", which read
						// as a sum of the rows above it, arithmetic that never holds,
						// because a roster size has nothing to do with how many role
						// assignments a run touched.
						["Reconcile roster", roles.rosterTotal],
					]}
					title="Roles"
				/>
			</div>
			{roles.removalsAborted ? (
				<div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
					<span className="font-medium text-amber-600">
						No members were removed:
					</span>{" "}
					<span className="text-muted-foreground">
						{removalAbortLabel(roles.removalsAborted)} The run stayed add-only;
						re-sync once the roster reads correctly.
					</span>
				</div>
			) : null}
			{roles.removedReport.length > 0 ? (
				<RemovedMembersList
					rows={roles.removedReport.map((m) => ({
						externalRef: m.externalRef,
						email: m.email,
						displayName: m.displayName,
						roles: m.rolesRemoved,
					}))}
					tense="did"
				/>
			) : null}
			{roles.heldReport.length > 0 ? (
				<HeldMembersList held={roles.heldReport} tense="did" />
			) : null}
			{emailsRebound.length > 0 ? (
				<EmailsReboundList rows={emailsRebound} tense="did" />
			) : null}
			{skipped.length > 0 ? <SkippedUsersList skipped={skipped} /> : null}
			{failed.length > 0 ? <FailedUsersList failed={failed} /> : null}
		</div>
	);
}

function StatGroup({
	title,
	rows,
}: {
	title: string;
	rows: Array<[label: string, value: number]>;
}) {
	return (
		<div>
			<div className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
				{title}
			</div>
			<dl className="space-y-1 text-sm">
				{rows.map(([label, value]) => (
					<div className="flex justify-between gap-4" key={label}>
						<dt className="text-muted-foreground">{label}</dt>
						<dd className="font-mono tabular-nums">{value}</dd>
					</div>
				))}
			</dl>
		</div>
	);
}

type CpuProfileResponse = {
	durationMs: number;
	sampleIntervalMs: number;
	sampleCount: number;
	distinctStacks: number;
	truncatedStacks: number;
	folded: string;
	topFunctions: Array<{
		frame: string;
		selfSamples: number;
		selfPct: number;
		totalSamples: number;
		totalPct: number;
	}>;
};

const PROFILE_DURATION_OPTIONS = [
	{ id: "15000", label: "15 seconds" },
	{ id: "30000", label: "30 seconds" },
	{ id: "60000", label: "60 seconds" },
] as const;

// Slack on top of the capture window for RPC transport + server aggregation.
const PROFILE_RPC_TIMEOUT_SLACK_MS = 30 * 1000;

function CpuProfileSection() {
	const [durationMs, setDurationMs] = useState<string>("15000");
	const [capturing, setCapturing] = useState(false);
	const [lastResult, setLastResult] = useState<CpuProfileResponse | null>(null);

	async function handleCapture() {
		setCapturing(true);
		try {
			const result = await ws.sendWithPromise<CpuProfileResponse>({
				id: ulid(),
				procedure: "instanceAdmin.captureCpuProfile",
				data: { durationMs: Number(durationMs) },
				timeoutMs: Number(durationMs) + PROFILE_RPC_TIMEOUT_SLACK_MS,
			});
			setLastResult(result);

			// Hand the collapsed stacks straight to the browser as a download. The
			// file drops into speedscope.app / flamegraph.pl as-is.
			const blob = new Blob([result.folded], { type: "text/plain" });
			const url = URL.createObjectURL(blob);
			const anchor = document.createElement("a");
			anchor.href = url;
			anchor.download = `cpu-profile-${new Date()
				.toISOString()
				.replaceAll(":", "-")}.folded.txt`;
			anchor.click();
			URL.revokeObjectURL(url);

			toast.success(
				`Captured ${result.sampleCount} samples (${result.distinctStacks} stacks) over ${result.durationMs / 1000}s`
			);
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Capture failed: ${message}`);
		} finally {
			setCapturing(false);
		}
	}

	return (
		<Card className="w-full max-w-lg">
			<CardHeader>
				<CardTitle>CPU profile</CardTitle>
				<CardDescription>
					Sample the server's CPU for a window and download the stacks (open the
					file in speedscope.app). Negligible overhead while sampling; the first
					capture leaves the sampler running (~1% of a core) until the server
					restarts.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="flex items-end gap-3">
					<div className="space-y-1">
						<Label htmlFor="cpu-profile-duration">Duration</Label>
						<Select
							disabled={capturing}
							onValueChange={setDurationMs}
							value={durationMs}
						>
							<SelectTrigger className="w-40" id="cpu-profile-duration">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{PROFILE_DURATION_OPTIONS.map((option) => (
									<SelectItem key={option.id} value={option.id}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<Button disabled={capturing} onClick={handleCapture}>
						{capturing
							? `Capturing ${Number(durationMs) / 1000}s...`
							: "Capture"}
					</Button>
				</div>
				{lastResult && lastResult.topFunctions.length > 0 && (
					<div>
						<div className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
							Top functions by self time
						</div>
						<dl className="space-y-1 text-sm">
							{lastResult.topFunctions.slice(0, 8).map((fn) => (
								<div className="flex justify-between gap-4" key={fn.frame}>
									<dt className="truncate font-mono text-xs" title={fn.frame}>
										{fn.frame}
									</dt>
									<dd className="shrink-0 font-mono text-xs tabular-nums">
										{fn.selfPct}%
									</dd>
								</div>
							))}
						</dl>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
